import type {
  Contact,
  ConversationItemList,
} from "../../services/generated/waba-api/models";

/**
 * A contact Meta only knows by business-scoped user ID has no phone number at all,
 * so anything phone-shaped has to be read through these helpers rather than
 * `contact.phoneNumber` directly.
 */
export type IdentityBearingContact = Pick<
  Contact,
  | "phoneNumber"
  | "waUsername"
  | "bsuid"
  | "displayIdentity"
  | "phoneReachable"
> & { whatsappReachable?: boolean };

export const BSUID_PATTERN = /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$/;

export function isPhoneReachable(
  contact: IdentityBearingContact | null | undefined,
): boolean {
  if (!contact) {
    return false;
  }
  if (contact.phoneReachable !== undefined) {
    return contact.phoneReachable;
  }
  return !!contact.phoneNumber?.trim();
}

export function isWhatsAppReachable(
  contact: IdentityBearingContact | null | undefined,
): boolean {
  if (!contact) {
    return false;
  }
  if (contact.whatsappReachable !== undefined) {
    return contact.whatsappReachable;
  }
  return (
    isPhoneReachable(contact) || BSUID_PATTERN.test(contact.bsuid?.trim() ?? "")
  );
}

export function conversationPhoneReachable(
  conversation: ConversationItemList | null | undefined,
): boolean {
  if (!conversation) {
    return false;
  }
  if (conversation.contactPhoneReachable !== undefined) {
    return conversation.contactPhoneReachable;
  }
  return !!conversation.contactNumberPhone?.trim();
}

/** Strips the leading `@` the backend does not store. */
export function normalizeUsername(
  value: string | null | undefined,
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
}

/**
 * Trims the free-text conversation search while preserving a leading `@`.
 * The API uses that prefix to restrict the query to `contacts.wa_username`.
 */
export function normalizeConversationSearchInput(
  value: string | null | undefined,
): string {
  return value?.trim() ?? "";
}

export function usernameHandle(
  contact: IdentityBearingContact | null | undefined,
): string | undefined {
  const username = normalizeUsername(contact?.waUsername);
  return username ? `@${username}` : undefined;
}

/**
 * Mirrors the backend `ContactIdentityFormatter` for contacts built client-side
 * (upload previews, form drafts) that never went through the API.
 */
export function contactDisplayIdentity(
  contact: IdentityBearingContact | null | undefined,
  fallback = "—",
): string {
  if (!contact) {
    return fallback;
  }
  if (contact.displayIdentity?.trim()) {
    return contact.displayIdentity;
  }
  const phone = contact.phoneNumber?.trim();
  if (phone) {
    return phone;
  }
  const handle = usernameHandle(contact);
  if (handle) {
    return handle;
  }
  const bsuid = contact.bsuid?.trim();
  if (bsuid) {
    return `WhatsApp user ·${bsuid.slice(-4)}`;
  }
  return fallback;
}
